import React, { useState, useEffect } from "react";
import PrepaymentType from "components/Calculators/Constants/PrepaymentType";

import { HarnessForm } from "..";
import { fieldTypes } from "../../../../constants";
import {
  dayCountTypes,
  prepaymentTypes,
  interestRateTypes,
  amortTypesShort,
  aggregateInterestMethods,
  fixedRateIndexes,
  curves
} from "../../Constants/DropDownLists";

const ExistingFinancingForm = props => {
  const { setFieldValue, values: formikValues } = props;
  const { efRateType, efAmortizationType, efPrepaymentType, efAggregateInterestMethod } = formikValues;
  const [ioPeriodFieldHidden, setIoPeriodFieldHidden] = useState(true);
  const [fixedRateTypeFieldsHidden, setFixedRateTypeFieldsHidden] = useState(true);
  const [floatingRateTypeFieldsHidden, setFloatingRateTypeFieldsHidden] = useState(true);
  const [openPeriodsFieldHidden, setOpenPeriodsFieldHidden] = useState(true);
  const [minimumPenaltyFieldHidden, setMinimumPenaltyFieldHidden] = useState(true);
  const [flatPercentFeeHidden, setFlatPercentFeeHidden] = useState(true);
  const [aggregateInterestMethodHidden, setAggregateInterestMethodHidden] = useState(true);
  const [aggregateInterestAmountHidden, setAggregateInterestAmountHidden] = useState(true);
  const [aggregateInterestMonthsOfInterestHidden, setAggregateInterestMonthsOfInterestHidden] = useState(true);
  const [rateIndexOptions, setRateIndexOptions] = useState([]);

  useEffect(() => {
    if (efRateType === "FIXED") {
      setFieldValue("efRateIndex", "");
      setFieldValue("efLoanSpread", "");
      setFixedRateTypeFieldsHidden(false);
      setFloatingRateTypeFieldsHidden(true);
    } else if (efRateType === "FLOATING") {
      setFieldValue("efRate", "");
      setFixedRateTypeFieldsHidden(true);
      setFloatingRateTypeFieldsHidden(false);
      setRateIndexOptions(efRateType === "FIXED" ? fixedRateIndexes : curves);
    }
  }, [efRateType]);

  useEffect(() => {
    setFieldValue("efIoPeriods", "");
    efAmortizationType === "INTEREST_ONLY" ? setIoPeriodFieldHidden(true) : setIoPeriodFieldHidden(false);
  }, [efAmortizationType]);

  useEffect(() => {
    setFieldValue("efFlatFeePercentage", "");
    setFieldValue("efOpenPeriods", "");
    setFieldValue("efMinimumPenalty", "");
    setFieldValue("efAggregateInterestMethod", "");

    if (efPrepaymentType === PrepaymentType.FLAT_PERCENTAGE) {
      setFlatPercentFeeHidden(false);
      setOpenPeriodsFieldHidden(true);
      setMinimumPenaltyFieldHidden(true);
      setAggregateInterestMethodHidden(true);
    }

    if (efPrepaymentType === PrepaymentType.MAKE_WHOLE || efPrepaymentType === PrepaymentType.YIELD_MAINTENANCE_AGENCY_STYLE || efPrepaymentType === PrepaymentType.YIELD_MAINTENANCE_TRADITIONAL) {
      setFlatPercentFeeHidden(true);
      setOpenPeriodsFieldHidden(false);
      setMinimumPenaltyFieldHidden(false);
      setAggregateInterestMethodHidden(true);
    }

    if (efPrepaymentType === PrepaymentType.DEFEASANCE || efPrepaymentType === PrepaymentType.SPREAD_MAINTENANCE) {
      setFlatPercentFeeHidden(true);
      setOpenPeriodsFieldHidden(false);
      setMinimumPenaltyFieldHidden(true);
      setAggregateInterestMethodHidden(true);
    }

    if (efPrepaymentType === PrepaymentType.AGGREGATE_INTEREST) {
      setFlatPercentFeeHidden(true);
      setOpenPeriodsFieldHidden(true);
      setMinimumPenaltyFieldHidden(true);
      setAggregateInterestMethodHidden(false);
    }
  }, [efPrepaymentType]);

  useEffect(() => {
    setFieldValue("efAggregateInterestAmount", "");
    setFieldValue("efAggregateInterestMonthsOfInterest", "");

    if (efAggregateInterestMethod === "DEFINED") {
      setAggregateInterestAmountHidden(false);
      setAggregateInterestMonthsOfInterestHidden(true);
    }

    if (efAggregateInterestMethod === "FORMULAIC") {
      setAggregateInterestAmountHidden(true);
      setAggregateInterestMonthsOfInterestHidden(false);
    }

    if (efAggregateInterestMethod === "") {
      setAggregateInterestAmountHidden(true);
      setAggregateInterestMonthsOfInterestHidden(true);
    }
  }, [efAggregateInterestMethod]);

  const fields = [
    {
      label: "Loan Amount",
      fieldName: "efLoanAmount",
      fieldType: fieldTypes.MONEY_INPUT,
      selectValue: "value",
      onChange: ({ target }) => {
        setFieldValue("efLoanAmount", target.value);
      }
    },
    {
      label: "Start Date",
      fieldName: "efStartDate",
      fieldType: fieldTypes.DATE_PICKER,
      selectValue: "value",
      onChange: e => {
        setFieldValue("efStartDate", e?._d);
      }
    },
    {
      label: "Loan Term (m)",
      fieldName: "efLoanTerm",
      fieldType: fieldTypes.INTEGER_INPUT,
      selectValue: "value",
      onChange: ({ target }) => {
        setFieldValue("efLoanTerm", target.value);
      }
    },
    {
      label: "Daycount",
      fieldName: "efDaycount",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      selectValue: "value",
      options: dayCountTypes,
      shortWidth: true,
      onChange: ({ value }) => {
        setFieldValue("efDaycount", value);
      }
    },
    {
      label: "Rate Type",
      fieldName: "efRateType",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      selectValue: "value",
      shortWidth: true,
      options: interestRateTypes,
      onChange: ({ value }) => {
        setFieldValue("efRateType", value);
      }
    },
    {
      label: "Rate",
      fieldName: "efRate",
      fieldType: fieldTypes.PERCENT_INPUT,
      hidden: fixedRateTypeFieldsHidden,
      selectValue: "value",
      onChange: ({ target }) => {
        setFieldValue("efRate", target.value);
      },
      decimalScale: 2
    },
    {
      label: "Rate Index",
      fieldName: "efRateIndex",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      shortWidth: true,
      options: rateIndexOptions,
      hidden: floatingRateTypeFieldsHidden,
      selectValue: "value",
      onChange: ({ value }) => {
        setFieldValue("efRateIndex", value);
      }
    },
    {
      label: "Loan Spread",
      fieldName: "efLoanSpread",
      fieldType: fieldTypes.PERCENT_INPUT,
      hidden: floatingRateTypeFieldsHidden,
      selectValue: "value",
      onChange: ({ target }) => {
        setFieldValue("efLoanSpread", target.value);
      }
    },
    {
      label: "Prepayment Type",
      fieldName: "efPrepaymentType",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      selectValue: "value",
      shortWidth: true,
      options: prepaymentTypes,
      onChange: ({ value }) => {
        setFieldValue("efPrepaymentType", value);
      }
    },
    {
      label: "Open Periods",
      fieldName: "efOpenPeriods",
      fieldType: fieldTypes.INTEGER_INPUT,
      selectValue: "value",
      hidden: openPeriodsFieldHidden,
      onChange: ({ target }) => {
        setFieldValue("efOpenPeriods", target.value);
      }
    },
    {
      label: "Minimum Penalty",
      fieldName: "efMinimumPenalty",
      fieldType: fieldTypes.PERCENT_INPUT,
      selectValue: "value",
      hidden: minimumPenaltyFieldHidden,
      onChange: ({ target }) => {
        setFieldValue("efMinimumPenalty", target.value);
      }
    },
    {
      label: "Flat Fee (%)",
      fieldName: "efFlatFeePercentage",
      fieldType: fieldTypes.PERCENT_INPUT,
      selectValue: "value",
      hidden: flatPercentFeeHidden,
      decimalScale: 2,
      onChange: ({ target }) => {
        setFieldValue("efFlatFeePercentage", target.value);
      }
    },
    {
      label: "Method",
      fieldName: "efAggregateInterestMethod",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      selectValue: "value",
      shortWidth: true,
      options: aggregateInterestMethods,
      hidden: aggregateInterestMethodHidden,
      onChange: ({ value }) => {
        setFieldValue("efAggregateInterestMethod", value);
      }
    },
    {
      label: "Amount",
      fieldName: "efAggregateInterestAmount",
      fieldType: fieldTypes.MONEY_INPUT,
      seletValue: "value",
      hidden: aggregateInterestAmountHidden,
      onChange: ({ target }) => {
        setFieldValue("efAggregateInterestAmount", target.value);
      }
    },
    {
      label: "Months of Interest",
      fieldName: "efAggregateInterestMonthsOfInterest",
      fieldType: fieldTypes.INTEGER_INPUT,
      seletValue: "value",
      hidden: aggregateInterestMonthsOfInterestHidden,
      onChange: ({ target }) => {
        setFieldValue("efAggregateInterestMonthsOfInterest", target.value);
      }
    },
    {
      label: "Amortization Type",
      fieldName: "efAmortizationType",
      fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
      selectValue: "value",
      shortWidth: true,
      options: amortTypesShort,
      onChange: ({ value }) => {
        setFieldValue("efAmortizationType", value);
      }
    },
    {
      label: "Amortization Term",
      fieldName: "efAmortizationTerm",
      fieldType: fieldTypes.INTEGER_INPUT,
      selectValue: "value",
      onChange: ({ target }) => {
        setFieldValue("efAmortizationTerm", target.value);
      }
    },
    {
      label: "I/O Periods",
      fieldName: "efIoPeriods",
      fieldType: fieldTypes.INTEGER_INPUT,
      selectValue: "value",
      hidden: ioPeriodFieldHidden,
      onChange: ({ target }) => {
        setFieldValue("efIoPeriods", target.value);
      }
    }
  ];

  return <HarnessForm title="Existing Financing" fields={fields} />;
};

export default ExistingFinancingForm;
